import React, { useState } from 'react';

// Minimal, safe Markdown renderer for AI output. Builds React elements only —
// never uses dangerouslySetInnerHTML, so model output cannot inject HTML/JS.
// Supports: headings, paragraphs, bullet/numbered lists, fenced code blocks (with copy),
// inline code, **bold**, *italic*, and http(s) links.

export const CopyButton: React.FC<{ text: string; label?: string; className?: string }> = ({ text, label = 'Copy', className }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={className || 'btn btn-sm btn-outline-secondary'}
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
      }}
    >
      <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`} />
      {copied ? 'Copied' : label}
    </button>
  );
};

export const CodeBlock: React.FC<{ code: string; lang?: string }> = ({ code, lang }) => (
  <div className="position-relative my-2">
    <div className="d-flex justify-content-between align-items-center px-2 py-1 bg-dark text-white-50 small rounded-top">
      <span>{lang || 'code'}</span>
      <CopyButton text={code} className="btn btn-sm btn-link text-white-50 p-0 text-decoration-none" />
    </div>
    <pre className="bg-dark text-light p-2 mb-0 rounded-bottom small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      <code>{code}</code>
    </pre>
  </div>
);

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  // Order matters: code first (no formatting inside), then links, bold, italic.
  const re = /(`[^`]+`)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*\s][^*]*)\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyPrefix}-${i++}`;
    if (m[1]) out.push(<code key={k} className="px-1 bg-light rounded">{m[1].slice(1, -1)}</code>);
    else if (m[2]) out.push(<a key={k} href={m[4]} target="_blank" rel="noopener noreferrer nofollow">{m[3]}</a>);
    else if (m[5]) out.push(<strong key={k}>{m[6]}</strong>);
    else if (m[7]) out.push(<em key={k}>{m[8]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const SafeMarkdown: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const lines = (text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    const fence = line.match(/^\s*```\s*([\w+-]*)\s*$/);
    if (fence) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) buf.push(lines[i++]);
      i++; // closing fence (or EOF while streaming)
      blocks.push(<CodeBlock key={key++} code={buf.join('\n')} lang={fence[1]} />);
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const cls = level <= 2 ? 'h6 fw-bold mt-3 mb-2' : 'fw-semibold mt-2 mb-1';
      blocks.push(<div key={key++} className={cls}>{renderInline(h[2], `h${key}`)}</div>);
      i++;
      continue;
    }
    if (/^\s*([-*•])\s+/.test(line) || /^\s*\d+[.)]\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? /^\s*\d+[.)]\s+/.test(lines[i]) : /^\s*([-*•])\s+/.test(lines[i]))) {
        items.push(lines[i].replace(ordered ? /^\s*\d+[.)]\s+/ : /^\s*([-*•])\s+/, ''));
        i++;
      }
      const children = items.map((it, n) => <li key={n}>{renderInline(it, `li${key}-${n}`)}</li>);
      blocks.push(ordered ? <ol key={key++} className="mb-2 ps-4">{children}</ol> : <ul key={key++} className="mb-2 ps-4">{children}</ul>);
      continue;
    }
    if (!line.trim()) { i++; continue; }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^\s*```/.test(lines[i]) && !/^#{1,4}\s/.test(lines[i])
      && !/^\s*([-*•])\s+/.test(lines[i]) && !/^\s*\d+[.)]\s+/.test(lines[i])) {
      para.push(lines[i++]);
    }
    blocks.push(
      <p key={key++} className="mb-2">
        {para.map((p, n) => (
          <React.Fragment key={n}>{n > 0 && <br />}{renderInline(p, `p${key}-${n}`)}</React.Fragment>
        ))}
      </p>
    );
  }
  return <div className={className}>{blocks}</div>;
};

export default SafeMarkdown;
